import { useMutation, useQueryClient } from '@tanstack/react-query'

import { API_URL } from '@/constants/env'

import { insertAtBeginningOfPage } from '@/helpers/api'
import { SweetspotError } from '@/helpers/error'

import { useHandleError } from '@/hooks/common/useErrorHandler'
import { markFilesIndexStartedInCache } from '@/hooks/files/cache/fileCacheSync'
import { userFilesQueryKeys } from '@/hooks/files/types'
import { organizationFilesQueryKeys } from '@/hooks/organization/types'
import { boostOrganizationFilesIndexPolling } from '@/hooks/organization/useOrganizationFilesIndexStatus'
import {
  pursuitActionsQueryKeys,
  pursuitFilesQueryKeys,
} from '@/hooks/pursuits/types'
import { boostPursuitFilesIndexPolling } from '@/hooks/pursuits/usePursuitFilesIndexStatus'
import { useAuthInfoWithThrow } from '@/hooks/user/useAuthInfoWithThrow'
import { wordTemplatesQueryKeys } from '@/hooks/word-templates/types'

import type { PaginatedResponse } from '@/types/Api'
import type { File } from '@/types/File'
import type { UUID } from '@/types/Id'

interface BaseSharepointFileImport {
  drive_id: string
  drive_item_id: string
}

interface SharepointOrganizationFileImport extends BaseSharepointFileImport {
  type: 'organization_file'
  organization_id: UUID
  folder_id?: UUID | null
  tag_ids?: UUID[]
}

interface SharepointPursuitFileImport extends BaseSharepointFileImport {
  type: 'pursuit_file'
  pursuit_id: UUID
  folder_id?: UUID | null
  tag_ids?: UUID[]
}

interface SharepointUserFileImport extends BaseSharepointFileImport {
  type: 'user_file'
  organization_id: UUID
}

interface SharepointWordTemplateFileImport extends BaseSharepointFileImport {
  type: 'word_template_file'
  organization_id: UUID
  cover_pages: number
  tag_ids?: UUID[]
}

export type SharepointFileImport =
  | SharepointOrganizationFileImport
  | SharepointPursuitFileImport
  | SharepointUserFileImport
  | SharepointWordTemplateFileImport

interface UploadOrganizationFilesArgs {
  orgId: UUID | ''
}

interface ImportSharepointFilesMutationArgs {
  organization_id: UUID
  files: SharepointFileImport[]
}

interface ImportSharepointFilesRequestBody {
  files: SharepointFileImport[]
}

const importSharepointFiles = async (
  req: ImportSharepointFilesMutationArgs,
  accessToken: string,
): Promise<PaginatedResponse<File>> => {
  const url = `${API_URL}/api/v1/organizations/${req.organization_id}/sharepoint/files/import`

  const body: ImportSharepointFilesRequestBody = {
    files: req.files,
  }

  const res = await fetch(url, {
    method: 'POST',
    body: JSON.stringify(body),
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  })

  if (!res.ok) {
    throw new SweetspotError({
      name: 'UPLOAD_FILE_ERROR',
      message: res.statusText,
      cause: {
        status: res.status,
      },
    })
  }

  // Backend now returns a FileList { count, has_more, data: UploadedFile[] }
  return (await res.json()) as PaginatedResponse<File>
}

export const useImportSharepointFiles = ({
  orgId,
}: UploadOrganizationFilesArgs) => {
  const { accessToken } = useAuthInfoWithThrow()
  const queryClient = useQueryClient()

  const { handleError } = useHandleError()

  return useMutation<
    PaginatedResponse<File>,
    Error,
    ImportSharepointFilesMutationArgs,
    PaginatedResponse<File>
  >({
    mutationFn: (req) => importSharepointFiles(req, accessToken),
    onError: (err, req, context) => {
      handleError(err)
    },
    onSuccess: (data, req) => {
      const organizationFilesByOrgId = new Map<UUID, File[]>()
      const pursuitFilesByPursuitId = new Map<UUID, File[]>()
      const wordTemplateFilesByOrgId = new Map<UUID, File[]>()
      const userFilesByOrgId = new Map<UUID, File[]>()

      for (const file of data.data) {
        switch (file.type) {
          case 'organization_file': {
            const orgId = file.organization_file?.organization_id
            if (!orgId) break
            organizationFilesByOrgId.set(orgId, [
              ...(organizationFilesByOrgId.get(orgId) ?? []),
              file,
            ])
            break
          }
          case 'word_template_file': {
            const orgId = file.word_template_file?.organization_id
            if (!orgId) break
            wordTemplateFilesByOrgId.set(orgId, [
              ...(wordTemplateFilesByOrgId.get(orgId) ?? []),
              file,
            ])
            break
          }
          case 'pursuit_file': {
            const pursuitId = file.pursuit_file?.pursuit_id
            if (!pursuitId) break
            pursuitFilesByPursuitId.set(pursuitId, [
              ...(pursuitFilesByPursuitId.get(pursuitId) ?? []),
              file,
            ])
            break
          }
          case 'user_file': {
            const orgId = file.user_file?.organization_id ?? req.organization_id
            if (!orgId) break
            userFilesByOrgId.set(orgId, [
              ...(userFilesByOrgId.get(orgId) ?? []),
              file,
            ])
            break
          }
          default:
            break
        }
      }

      for (const [orgId, files] of organizationFilesByOrgId) {
        const queryKey = organizationFilesQueryKeys.organization({ orgId })
        queryClient.setQueryData<PaginatedResponse<File>>(queryKey, (oldData) =>
          insertAtBeginningOfPage(
            { data: files, count: files.length, has_more: false },
            oldData,
          ),
        )
        markFilesIndexStartedInCache({
          queryClient,
          fileIds: files.map((file) => file.id),
          orgId,
        })
        boostOrganizationFilesIndexPolling({
          queryClient,
          orgId,
        })
      }

      for (const [orgId, files] of wordTemplateFilesByOrgId) {
        const queryKey = wordTemplatesQueryKeys.organization({ orgId })
        queryClient.setQueryData<PaginatedResponse<File>>(queryKey, (oldData) =>
          insertAtBeginningOfPage(
            { data: files, count: files.length, has_more: false },
            oldData,
          ),
        )
        queryClient.invalidateQueries({ queryKey })
      }

      for (const [pursuitId, files] of pursuitFilesByPursuitId) {
        const queryKey = pursuitFilesQueryKeys.pursuit({ pursuitId })
        queryClient.setQueriesData<PaginatedResponse<File>>(
          { queryKey },
          (oldData) =>
            insertAtBeginningOfPage(
              { data: files, count: files.length, has_more: false },
              oldData,
            ),
        )

        const actionsKey = pursuitActionsQueryKeys.pursuit({ pursuitId })
        queryClient.invalidateQueries({ queryKey: actionsKey })
        markFilesIndexStartedInCache({
          queryClient,
          fileIds: files.map((file) => file.id),
          pursuitId,
        })
        boostPursuitFilesIndexPolling({
          queryClient,
          pursuitId,
        })
      }

      for (const [orgId, files] of userFilesByOrgId) {
        const queryKey = userFilesQueryKeys.organization({ orgId })
        queryClient.setQueryData<PaginatedResponse<File>>(queryKey, (oldData) =>
          insertAtBeginningOfPage(
            { data: files, count: files.length, has_more: false },
            oldData,
          ),
        )
        queryClient.invalidateQueries({
          queryKey,
        })
      }
    },
  })
}
